"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * One conversation for the whole site, shared by the FAQ section and the
 * floating chat, so a visitor can move between them without losing it.
 *
 * A chip answers at once from the written FAQ. A typed question goes to
 * /api/chat. If that is unavailable (no key, rate limit, network), the typed
 * question is matched to the closest FAQ entry instead, so the chat always
 * responds and never says anything the written FAQ does not.
 *
 * Every answer carries follow-up questions (`next`). A typed question gets them
 * from /api/chat/next, just after the answer; a chip or a fallback answer takes
 * related FAQ questions.
 */

export type Faq = { q: string; a: string };
export type Source = { title: string; url: string };
/** `private` messages stay in the browser and are never sent to the AI. */
export type Msg = {
  from: "you" | "bot";
  text: string;
  link?: boolean;
  sources?: Source[];
  private?: boolean;
  next?: string[];
  /** Follow-up questions are on their way, so their space is held. */
  nextLoading?: boolean;
  /** Offer to connect with the team beside this answer. */
  offer?: boolean;
};

type Lead = { name: string; email: string; company: string; focus: string; trials: string; message: string };

export type Step = {
  key: keyof Lead | "confirm";
  ask: (lead: Partial<Lead>) => string;
  placeholder?: string;
  autoComplete?: string;
  replies?: string[];
};

/**
 * The contact questions: the same details as the /demo form, one at a time. A
 * fixed script in the browser, not the AI, so the details never reach the model.
 */
const STEPS: Step[] = [
  {
    key: "name",
    ask: () => "Happy to connect you with the team. Just a few quick questions. What\u2019s your name?",
    placeholder: "Your name",
    autoComplete: "name",
  },
  {
    key: "email",
    ask: (l) => `Thanks, ${(l.name ?? "").split(/\s+/)[0]}. What\u2019s your work email?`,
    placeholder: "you@company.com",
    autoComplete: "email",
  },
  {
    key: "company",
    ask: () => "Which company or organisation are you with?",
    placeholder: "Company",
    autoComplete: "organization",
  },
  {
    key: "focus",
    ask: () => "What kind of studies do you run?",
    placeholder: "Or type your own",
    replies: ["BA-BE / Bioequivalence", "Generics development", "Phase I", "Phase II / III", "Phase IV", "Academic"],
  },
  {
    key: "trials",
    ask: () => "How many active trials do you have?",
    placeholder: "Or type a number",
    replies: ["1-3", "4-10", "11-25", "25+"],
  },
  {
    key: "message",
    ask: () => "Anything we should know? For example your current EDC or timelines.",
    placeholder: "Add a note",
    replies: ["Skip"],
  },
  {
    key: "confirm",
    ask: (l) =>
      `Here\u2019s what I\u2019ll send the team:\n${l.name}, ${l.email}\n${l.company}\n${l.focus}, ${l.trials} active trials${
        l.message ? `\n${l.message}` : ""
      }\n\nShall I send it?`,
    placeholder: "Send it, or Cancel",
    replies: ["Send it"],
  },
];

/** Offer to connect beside this many answers in, once per visit. */
const OFFER_AFTER = 3;
/** A stalled request gives way to the written FAQ rather than leaving the dots running. */
const ANSWER_TIMEOUT_MS = 25_000;
const NEXT_TIMEOUT_MS = 10_000;

type Chat = {
  log: Msg[];
  /** Waiting for the first word of an answer. */
  typing: boolean;
  /** An answer is still arriving. */
  busy: boolean;
  ask: (question: string, faqs: Faq[], known?: Faq) => void;
  /** What the input box sends: an answer if contact questions are running, otherwise a question. */
  send: (text: string, faqs: Faq[]) => void;
  startContact: () => void;
  /** The current contact question, if one is running. */
  step: Step | null;
  /** Quick answers to the current contact question. */
  replies: string[];
  /** Where the contact questions have got to, such as "Question 2 of 6". */
  progress: string | null;
};

const ChatContext = createContext<Chat | null>(null);

export function useChat() {
  const chat = useContext(ChatContext);
  if (!chat) throw new Error("useChat must be used inside ChatProvider");
  return chat;
}

const GREETING: Msg = {
  from: "bot",
  text: "Hi, I'm ION. What would you like to know about Trialion?",
  private: true,
};
const NO_ANSWER = "I don't have a good answer to that one. A person on our team will, on a 30-minute walkthrough.";

const STOP = new Set(
  "a an the and or of to in on for with is are do does did can could we you your our i it be by at as from this that what which how when who why will would there their any about have has me my us".split(
    " ",
  ),
);

/** Words a data manager uses for the same thing, folded to one stem. */
const SAME: Record<string, string> = {
  chang: "amend", modify: "amend", modif: "amend", updat: "amend",
  enroll: "enrol", enrolment: "enrol", enrollment: "enrol", enrolled: "enrol",
  mandatory: "ai", requir: "ai", artificial: "ai",
  blind: "design", blinded: "design", randomis: "design", randomiz: "design",
  sandbox: "environment", uat: "environment", test: "environment",
};

function words(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => (w.length > 2 || w === "ai") && !STOP.has(w))
    .map((w) => (w.length > 5 ? w.replace(/(ations?|ating|ated|ates?|ings?|ed|es|e|s)$/, "") : w.replace(/s$/, "")))
    .map((w) => SAME[w] ?? w);
}

/**
 * The best entry for a typed question, or null when nothing shares enough.
 * A word in an entry's question counts three times one in its answer, and a
 * word that appears in few entries counts more than one in many, so
 * "validate my study" finds validation rather than any entry with "study".
 */
function match(faqs: Faq[], text: string): Faq | null {
  const asked = new Set(words(text));
  if (!asked.size) return null;
  const docs = faqs.map((f) => ({ f, q: new Set(words(f.q)), a: new Set(words(f.a)) }));
  const rarity = (w: string) => {
    const df = docs.filter((d) => d.q.has(w) || d.a.has(w)).length;
    return df ? Math.log(1 + faqs.length / df) : 0;
  };
  let best: Faq | null = null;
  let top = 0;
  for (const d of docs) {
    let score = 0;
    asked.forEach((w) => {
      if (d.q.has(w)) score += 3 * rarity(w);
      else if (d.a.has(w)) score += rarity(w);
    });
    score /= Math.sqrt(asked.size);
    if (score > top) {
      top = score;
      best = d.f;
    }
  }
  return top >= 1.2 ? best : null;
}

/**
 * Drops a sentence that says what an earlier one already said. The model now and
 * then repeats itself when it cites the same passage twice, even when told not to.
 */
export function withoutRepeats(text: string) {
  const kept: { sentence: string; seen: Set<string> }[] = [];
  for (const sentence of text.split(/(?<=[.?!])\s+/)) {
    const seen = new Set(words(sentence));
    const repeat = kept.some((k) => {
      const shared = [...seen].filter((w) => k.seen.has(w)).length;
      return seen.size > 0 && shared / new Set([...seen, ...k.seen]).size >= 0.6;
    });
    if (!repeat) kept.push({ sentence, seen });
  }
  const clean = kept.map((k) => k.sentence).join(" ");
  // If an answer ran out of room, end on its last whole sentence, never mid-word.
  const end = Math.max(clean.lastIndexOf("."), clean.lastIndexOf("?"), clean.lastIndexOf("!"));
  return end > 0 ? clean.slice(0, end + 1) : clean;
}

/** Up to three FAQ questions not yet asked, those sharing the most words with this exchange first. */
function relatedFaqs(faqs: Faq[], log: Msg[], exchange: string): string[] {
  const asked = new Set(log.filter((m) => m.from === "you").map((m) => m.text.trim().toLowerCase()));
  const topic = new Set(words(exchange));
  return faqs
    .filter((f) => !asked.has(f.q.toLowerCase()))
    .map((f) => ({ q: f.q, score: words(`${f.q} ${f.a}`).filter((w) => topic.has(w)).length }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((f) => f.q);
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const [log, setLog] = useState<Msg[]>([GREETING]);
  const [typing, setTyping] = useState(false);
  const [busy, setBusy] = useState(false);
  // The latest conversation, readable from inside a running request.
  const logRef = useRef(log);
  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const lead = useRef<Partial<Lead>>({});
  const answers = useRef(0);
  const offered = useRef(false);
  const update = useCallback((fn: (l: Msg[]) => Msg[]) => {
    logRef.current = fn(logRef.current);
    setLog(logRef.current);
  }, []);

  /** A line from ION in the contact script, after a short pause. */
  const say = useCallback(
    (text: string, then?: () => void) => {
      setBusy(true);
      setTyping(true);
      const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.setTimeout(
        () => {
          update((l) => [...l, { from: "bot", text, private: true }]);
          setTyping(false);
          setBusy(false);
          then?.();
        },
        still ? 0 : 450,
      );
    },
    [update],
  );

  const startContact = useCallback(() => {
    offered.current = true;
    lead.current = {};
    say(STEPS[0].ask({}), () => setStepIndex(0));
  }, [say]);

  /** After an answer, once per visit, offer to connect, without interrupting the reading. */
  const maybeOffer = useCallback(() => {
    answers.current += 1;
    if (offered.current || answers.current < OFFER_AFTER) return;
    offered.current = true;
    update((l) => [...l.slice(0, -1), { ...l[l.length - 1], offer: true }]);
  }, [update]);

  const answerStep = useCallback(
    async (raw: string) => {
      if (stepIndex === null) return;
      const text = raw.trim();
      update((l) => [...l, { from: "you", text, private: true }]);

      if (/^cancel$/i.test(text)) {
        setStepIndex(null);
        lead.current = {};
        say("No problem, nothing was sent. Ask me anything else.");
        return;
      }

      const step = STEPS[stepIndex];
      if (step.key === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(text)) {
        say("That doesn\u2019t look like an email address. Could you check it?");
        return;
      }

      if (step.key === "confirm") {
        if (!/^(send|send it|yes|y|ok|okay)$/i.test(text)) {
          say("Say \u201cSend it\u201d to send this to the team, or \u201cCancel\u201d to leave it.");
          return;
        }
        setStepIndex(null);
        setBusy(true);
        setTyping(true);
        const l = lead.current as Lead;
        let reply = `Thanks, ${l.name.split(/\s+/)[0]}. The team will be in touch within 1 business day.`;
        try {
          // Loaded on use: most visitors never send this.
          const { sendContactEmail } = await import("@/lib/contactEmail");
          await sendContactEmail({
            name: l.name,
            email: l.email,
            company: l.company,
            trials: l.trials,
            message: `Sent from the ION chat.\nStudy focus: ${l.focus}\n\n${l.message}`,
          });
        } catch (err) {
          console.error(err);
          reply = "Sorry, that didn\u2019t send. Please email hello@trialion.com and the team will pick it up.";
        }
        lead.current = {};
        setTyping(false);
        setBusy(false);
        update((m) => [...m, { from: "bot", text: reply, private: true }]);
        return;
      }

      lead.current = { ...lead.current, [step.key]: step.key === "message" && /^skip$/i.test(text) ? "" : text };
      const next = stepIndex + 1;
      say(STEPS[next].ask(lead.current), () => setStepIndex(next));
    },
    [say, stepIndex, update],
  );

  const ask = useCallback(
    async (question: string, faqs: Faq[], known?: Faq) => {
      const text = question.trim();
      if (!text) return;
      update((l) => [...l, { from: "you", text }]);
      setBusy(true);
      setTyping(true);

      const finish = (reply?: Msg) => {
        if (reply) update((l) => [...l, reply]);
        setTyping(false);
        setBusy(false);
      };

      const related = (answer: string) => relatedFaqs(faqs, logRef.current, `${text} ${answer}`);

      if (known) {
        const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        window.setTimeout(() => {
          finish({ from: "bot", text: known.a, next: related(known.a) });
          maybeOffer();
        }, still ? 0 : 250);
        return;
      }

      const fallback = (): Msg => {
        const hit = match(faqs, text);
        return hit
          ? { from: "bot", text: hit.a, next: related(hit.a) }
          : { from: "bot", text: NO_ANSWER, link: true, next: related("") };
      };

      const messages = logRef.current
        .filter((m) => !m.private)
        .map((m) => ({ role: m.from === "you" ? "user" : "assistant", content: m.text }));

      let started = false;
      // The answer's place in the log: a new question may follow it before its
      // follow-up questions arrive.
      let answerAt = -1;

      try {
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages }),
          signal: AbortSignal.timeout(ANSWER_TIMEOUT_MS),
        });
        if (!res.ok || !res.body) return finish(fallback());

        let failed = false;
        let wantsContact = false;
        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = "";
        const patch = (fn: (m: Msg) => Msg) => update((l) => [...l.slice(0, -1), fn(l[l.length - 1])]);
        const setNext = (next: string[]) =>
          update((l) => l.map((m, i) => (i === answerAt ? { ...m, next, nextLoading: false } : m)));

        /** The answer is complete, so the visitor can carry on while its follow-ups load. */
        const conclude = () => {
          if (answerAt >= 0 || !started || failed || wantsContact) return;
          answerAt = logRef.current.length - 1;
          patch((m) => ({ ...m, text: withoutRepeats(m.text), link: /walkthrough|demo/i.test(m.text), nextLoading: true }));
          finish();
          maybeOffer();
        };

        const handle = (line: string) => {
          if (!line.trim()) return;
          const e = JSON.parse(line) as { t: string; v?: string; title?: string; url?: string };
          if (e.t === "text" && e.v) {
            if (!started) {
              started = true;
              setTyping(false);
              update((l) => [...l, { from: "bot", text: e.v as string }]);
            } else {
              patch((m) => ({ ...m, text: m.text + e.v }));
            }
          } else if (e.t === "cite" && e.title && e.url && started) {
            const source = { title: e.title, url: e.url };
            patch((m) => ({ ...m, sources: [...(m.sources ?? []), source] }));
          } else if (e.t === "contact") {
            wantsContact = true;
          } else if (e.t === "error") {
            failed = true;
          } else if (e.t === "done") {
            conclude();
          }
        };

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += value;
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          lines.forEach(handle);
        }
        handle(buffer);

        if (failed) {
          // Replace a partial answer rather than leave it cut off.
          if (started) update((l) => l.slice(0, -1));
          return finish(fallback());
        }
        if (wantsContact) {
          finish();
          startContact();
          return;
        }
        if (!started) return finish(fallback());
        conclude();

        const answer = logRef.current[answerAt]?.text ?? "";
        let next: string[] = [];
        try {
          const more = await fetch("/api/chat/next", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ question: text, answer }),
            signal: AbortSignal.timeout(NEXT_TIMEOUT_MS),
          });
          if (more.ok) next = ((await more.json()) as { next?: string[] }).next ?? [];
        } catch {}
        // No follow-ups from the AI, so use the written FAQ.
        setNext(next.length ? next : related(answer));
      } catch {
        // Replace a partial answer rather than leave it cut off.
        if (started && answerAt < 0) update((l) => l.slice(0, -1));
        finish(fallback());
      }
    },
    [maybeOffer, startContact, update],
  );

  const send = useCallback(
    (text: string, faqs: Faq[]) => {
      if (stepIndex !== null) {
        answerStep(text);
      } else {
        ask(text, faqs);
      }
    },
    [answerStep, ask, stepIndex],
  );

  const step = stepIndex === null ? null : STEPS[stepIndex];
  const replies = step ? [...(step.replies ?? []), "Cancel"] : [];
  // The last step only confirms, so it isn't counted as a question.
  const questions = STEPS.length - 1;
  const progress = stepIndex !== null && stepIndex < questions ? `Question ${stepIndex + 1} of ${questions}` : null;

  const value = useMemo(
    () => ({ log, typing, busy, ask, send, startContact, step, replies, progress }),
    // `replies` and `progress` are rebuilt each render but only change with `step`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [log, typing, busy, ask, send, startContact, step],
  );
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
